import { useAuth0 } from '@auth0/auth0-react'
import { Grid } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useUserRole } from '../../hooks/useUserRole'
import { farmQueryOptions } from '../../hooks/data/useFarm'
import { useMonitoringSites } from '../../hooks/data/useMonitoringSites'
import { perimeterPreconditionsQueryOptions } from '../../hooks/data/usePerimeterPreconditions'
import { processValidation } from '../../utils/Errors/validations'
import { contactSupportReasons } from '../GlobalLoadingModal/GlobalLoadingModal'
import PerimeterMap from './PerimeterMap'
import PerimeterValidationPanel from './PerimeterValidationPanel'

// A VALUE identity, not a reference: `farm` and the site list are query results whose identity
// changes on every refetch, and re-seeding on identity would discard an in-progress perimeter edit.
// The roles are part of it because useUserRole only resolves them after the first render.
const perimeterSeedSignature = ({ farm, monitoringSites, isViewOnly, overrideEdition }) =>
  [
    farm.id,
    farm.isPerimeterValidated,
    JSON.stringify(farm.geometry ?? null),
    isViewOnly,
    overrideEdition,
    ...monitoringSites
      .filter(site => site.isValidationSite)
      .map(site => `${site.id}:${site.name}:${JSON.stringify(site.location ?? null)}`),
  ].join('|')

const PerimeterHome = ({ farm, mapWidth, mapHeight }) => {
  const [mapReady, setMapReady] = useState(false)
  const [mapActions, setMapActions] = useState(null)
  const [mapData, setMapData] = useState({
    features: null,
    hasPerimeter: null,
    featuresUpdated: null,
    canValidate: null,
  })
  const { isViewOnly, isAdmin } = useUserRole()

  const isPerimeterValidated = farm ? farm.isPerimeterValidated : null
  const overrideEdition = isAdmin

  const { user } = useAuth0()
  const [highlightedPoint, setHighlightedPoint] = useState(null)

  const { data: monitoringSites } = useMonitoringSites({ farmId: farm?.id })
  const queryClient = useQueryClient()
  const seededSignatureRef = useRef(null)
  const latestSeedRef = useRef(0)

  useEffect(() => {
    // Gates on monitoringSites so the map doesn't build with an empty site list while RQ is still fetching.
    if (!farm || !monitoringSites) return

    const signature = perimeterSeedSignature({
      farm,
      monitoringSites,
      isViewOnly,
      overrideEdition,
    })
    if (seededSignatureRef.current === signature) return
    seededSignatureRef.current = signature
    latestSeedRef.current += 1
    const seed = latestSeedRef.current

    const loadExtraData = async () => {
      const updateFarm = await queryClient.fetchQuery(farmQueryOptions({ farmId: farm.id, user }))

      const perimeter = updateFarm.toGeoJSON
      const newPerimeter = {
        ...perimeter,
        properties: {
          ...perimeter.properties,
          type: 'perimeter',
          originalGeometry: perimeter.geometry,
        },
        style: { ...perimeter.style, color: 'green' },
      }

      const validationMonitorSites = monitoringSites
        .filter(site => site.isValidationSite)
        .map(validationSite => {
          return {
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [validationSite.location[1], validationSite.location[0]],
            },
            properties: {
              id: validationSite.id,
              name: validationSite.name,
            },
            style: { color: 'green' },
          }
        })
      const newFeatures = [newPerimeter, ...validationMonitorSites].sort((a, b) =>
        a.properties.name.localeCompare(b.properties.name),
      )

      let updatedCanValidate = false
      if (!isViewOnly) {
        updatedCanValidate = await processValidation({
          contactSupportReasonType: contactSupportReasons.PerimeterValidationsPreconditionFailed,
          doAction: async () =>
            queryClient.fetchQuery(
              perimeterPreconditionsQueryOptions({ farmId: updateFarm.id, overrideEdition }),
            ),
        })
      }

      // The role resolves after the first render and the non-admin preconditions check is slower,
      // so an older seed can resolve after a newer one; only the newest seed may commit.
      if (seed !== latestSeedRef.current) return

      setMapData({
        seed,
        features: newFeatures,
        hasPerimeter: !updateFarm.geometry,
        featuresUpdated: newFeatures,
        canValidate: updatedCanValidate,
      })
    }

    loadExtraData()
  }, [farm, isViewOnly, monitoringSites, overrideEdition, queryClient, user])

  const { seed, features, featuresUpdated, canValidate } = mapData

  const setFeatures = useCallback(newFeats => setMapData(s => ({ ...s, features: newFeats })), [])
  const setFeaturesUpdated = useCallback(
    newFeatsUpdated => setMapData(s => ({ ...s, featuresUpdated: newFeatsUpdated })),
    [],
  )

  return (
    features &&
    isPerimeterValidated !== null && (
      <Grid container>
        <Grid item xs={4}>
          {/* A re-seed restarts the validation process: a panel that outlived it would re-validate
              the new seed and re-enable editing on the redrawn map while showing "Iniciar proceso". */}
          <PerimeterValidationPanel
            key={seed}
            canValidate={canValidate}
            featuresUpdated={featuresUpdated}
            isPerimeterValidated={isPerimeterValidated}
            mapReady={mapReady}
            overrideEdition={overrideEdition}
            setFeatures={setFeatures}
            setHighlightedPoint={setHighlightedPoint}
            setMapActions={setMapActions}
          />
        </Grid>
        <Grid item xs={8}>
          <PerimeterMap
            features={features}
            highlightedPoint={highlightedPoint}
            mapActions={mapActions}
            mapHeight={mapHeight}
            mapWidth={mapWidth}
            seed={seed}
            setFeaturesUpdated={setFeaturesUpdated}
            setMapReady={setMapReady}
          />
        </Grid>
      </Grid>
    )
  )
}

export default PerimeterHome
